'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { useUserStore } from '@/zustand-stores/userStore';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { user: appUser } = useUserStore();

  // Initialize PostHog
  useEffect(() => {
    const isProduction = process.env.NODE_ENV === 'production';
    const shouldInitialize = isProduction || process.env.NEXT_PUBLIC_ENABLE_ANALYTICS_IN_DEV === 'true';

    if (shouldInitialize) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host: '/ingest',
        ui_host: 'https://us.posthog.com',
        capture_pageview: true,
        capture_exceptions: true,
        capture_heatmaps: true,
        capture_performance: true,
        autocapture: true,
        persistence: 'localStorage',
        debug: process.env.NODE_ENV === 'development',
        // Enhanced error tracking configuration
        enable_recording_console_log: true,
        mask_all_text: false,
        mask_all_element_attributes: false,
        session_recording: {
          maskAllInputs: false,
          maskTextSelector: '.mask-text',
          maskInputOptions: {
            password: true,
            email: false,
            tel: false,
            number: false,
          },
        },
      });
    }

    return () => {
      if (shouldInitialize) {
        posthog.reset();
      }
    };
  }, []);

  // Identify user when available
  useEffect(() => {
    if (appUser) {
      const userData = {
        id: appUser.id,
        email: appUser.email,
        name: `${appUser.first_name || ''} ${appUser.last_name || ''}`.trim(),
        created_at: appUser.created_at,
        last_login: appUser.last_login,
        last_active: appUser.last_active,
      };
      posthog.identify(appUser.id, userData);

      // Also capture this as an event to ensure data is flowing
      posthog.capture('user_identified', userData);
    }
  }, [appUser]);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
